
    // ============================================================
    // Ramp Calculator — ENGINE + SELF-TEST (pure, DOM-free)
    // Implementation of apps/ramp-calc/rules.md v0.3.
    // If the maths changes, change rules.md FIRST, then this file.
    //
    // Core formula (rules.md § 1a):
    //   required_length = rise × √(1 + n²)      (ratio form, target 1:n)
    //   required_length = rise ÷ sin(θ)         (angle form, Pet mode)
    // Reverse (§ 1b):
    //   achieved_angle = asin(rise ÷ L) ; achieved_run = √(L² − rise²)
    //   achieved_ratio_n = achieved_run ÷ rise  → report as 1:n
    // ============================================================

    'use strict';

    const RAMP = (() => {
      const DEG = 180 / Math.PI;
      // AS1428.1 cl. 10.3(b)'s 3% is a CONSISTENCY allowance along the length, not a
      // compliance margin (§ 4a). It no longer widens any pass band; it survives only
      // to tell a "just misses" red from a "nowhere near" red, which is copy, not a
      // verdict.
      const TOLERANCE = 0.03;
      // AS1428.1 cl. 10.3(c)(i): on a 1:14 ramp, landings at intervals not greater
      // than 9 m. Measured along the RUN (the horizontal), not the ramp length: at
      // 1:14 that's a 643 mm rise, i.e. any normal house entry. Step ramps (cl. 10.4,
      // max length 1900 mm) and threshold ramps (def 4.18, max 280 mm) can never
      // reach it, so this only binds the main ramp.
      const LANDING_MAX_RUN = 9000;        // mm of run between landings at 1:14
      const THRESHOLD_MAX_RISE = 35;       // mm, above this, threshold → step ramp (§ 7)
      const STEP_RAMP_MAX_RISE = 190;      // mm, AS1428.1 step ramp ceiling
      const PET_TALL_RISE = 700;           // mm, "tall climb for a dog" note (§ 7)
      const CAPACITY_MARGIN = 1.2;         // warn when rated capacity < load × 1.2 (§ 4b)
      const QUOTE_CAPACITY_KG = 3000;      // plant quote-fork trigger: ≥ 3 t (§ 5c)

      // Mode / sub-case targets (rules.md § 2, § 2a–2c)
      const SUBCASES = {
        // PLAIN (v0.4) — the default. No context, no target, no verdict:
        // rise + ramp length → angle. This is the tool 939 people/quarter
        // search for as "ramp angle calculator". Never gated behind a choice.
        plain: {
          any: { plain: true, label: 'Any ramp' },
        },
        access: {
          unassisted: { n: 14, label: 'Occupied wheelchair, unassisted',
                        std: 'AS 1428.1:2021 main ramp, 1:14' },
          assisted:   { n: 10, label: 'Assisted (carer pushing) / short rise ≤ 190 mm',
                        std: 'AS1428.1 step ramp, 1:10, rise up to 190 mm' },
        },
        loading: {
          // Band sub-cases (rules.md § 2b v0.3): green/amber ceilings in degrees,
          // anchored to RC catalogue medians. No loading standard exists — copy
          // says "typical working range", never "compliant".
          driveup:  { band: { green: 22, amber: 30 }, label: 'Drive-up, motorbike, ATV, ride-on mower' },
          car:      { band: { green: 10, amber: 14 }, label: 'Car / low-clearance, onto a trailer' },
          walkup:   { band: { green: 15, amber: 20 }, label: 'Walk-up, trolley, wheelbarrow, removals, livestock' },
          // Plant + forklift ceilings are RC's OWN published figures (rules.md § 2b-1):
          // skid-steer blog "a gradient between 16 and 23 degrees"; forklift blog
          // "no more than 12°". Ambers are house judgement. A ramp has no rated
          // gradient, a machine does, so `rated_max_gradient_n` is gone (v0.6).
          plant:    { band: { green: 23, amber: 30 }, label: 'Plant / machinery, excavator, skid-steer (~1–13 t)' },
          forklift: { band: { green: 12, amber: 15 }, quote: true,
                      label: 'Forklift / yard / container / dock' },
        },
        pet: {
          senior: { deg: 18, label: 'Senior / small / post-op dog, max 18°' },
          young:  { deg: 25, label: 'Young / large / able dog, max 25°' },
        },
        threshold: {
          doorway: { n: 8, label: 'Doorway threshold, rise ≤ 35 mm, 1:8' },
        },
      };

      const requiredLenRatio = (rise, n) => rise * Math.sqrt(1 + n * n);
      const requiredLenAngle = (rise, deg) => rise / Math.sin(deg / DEG);
      const targetAngleFromN = (n) => Math.atan(1 / n) * DEG;
      function achieved(rise, L) {
        const run = Math.sqrt(L * L - rise * rise);
        return { angle_deg: Math.asin(rise / L) * DEG, run_mm: run, ratio_n: run / rise };
      }

      // Main calculation — input/output per rules.md § 6.
      // inp: { mode, sub, rise_mm, length_mm?, load_kg?, capacity_kg?, machine_max_deg? }
      function calc(inp) {
        const flags = [];
        const out = {
          mode: inp.mode, sub_case: inp.sub, rise_mm: inp.rise_mm,
          target: null, required_length_mm: null,
          verdict: null, capacity: null, achieved: null,
          quote_fork: false, block_cart: false, flags,
          diagram: null, effective_sub: inp.sub,
        };

        const rise = Number(inp.rise_mm);
        if (!(rise > 0)) { flags.push({ kind: 'error', msg: 'Enter a rise above 0 mm.' }); return out; }

        let mode = inp.mode, sub = inp.sub;
        let sc = (SUBCASES[mode] || {})[sub];
        if (!sc) { sub = Object.keys(SUBCASES[mode])[0]; sc = SUBCASES[mode][sub]; out.effective_sub = sub; }

        // Resolve the target: ratio n, max angle θ, or none (plain — § 2 v0.4)
        let n = null, thetaDeg = null;
        if (mode === 'plain') {
          // No target. Needs a candidate length to say anything.
          const Lp = Number(inp.length_mm);
          if (!(Lp > 0)) {
            out.needs_length = true;
            return out;
          }
          if (Lp <= rise) {
            flags.push({ kind: 'error', msg: 'A ramp can’t be shorter than the height it climbs.' });
            return out;
          }
          const ap = achieved(rise, Lp);
          out.achieved = ap;
          out.diagram = { rise_mm: rise, run_mm: ap.run_mm, length_mm: Lp, angle_deg: ap.angle_deg };
          return out;
        }
        if (mode === 'pet') {
          thetaDeg = sc.deg;
          if (rise > PET_TALL_RISE) {
            flags.push({ kind: 'warn', msg: 'That’s a tall climb for a dog, consider steps or a landing.' });
          }
        } else if (mode === 'access' && sub === 'assisted' && rise > STEP_RAMP_MAX_RISE) {
          // 1:10 is the STEP-RAMP gradient, and AS1428.1 caps a step ramp at a 190 mm
          // rise (def 4.17: "a maximum rise of 190 mm... and a gradient not steeper
          // than 1 in 10"). The standard has no carer/assisted concession at all: we
          // invented one and then applied it at ANY rise with no guard, so a 900 mm
          // entry rendered GREEN badged "AS1428.1 1:10" with no warning. Above the
          // step-ramp ceiling the only honest target is the main ramp gradient.
          // Threshold mode already guarded this at 35/190; access never did.
          n = 14; out.effective_sub = 'unassisted';
          flags.push({ kind: 'warn',
            msg: 'Over 190 mm is past step-ramp territory, so 1:10 no longer applies. Recomputed at the main ramp gradient 1:14.' });
        } else if (mode === 'threshold') {
          n = sc.n;
          if (rise > THRESHOLD_MAX_RISE) {
            n = 10; out.effective_sub = 'step_ramp';
            flags.push({ kind: 'warn',
              msg: 'Over 35 mm is a step ramp, not a threshold ramp, recomputed at the AS1428.1 step-ramp gradient 1:10.' });
            if (rise > STEP_RAMP_MAX_RISE) {
              // Same guard as access/assisted above. This used to WARN "switch to
              // Access mode (1:14)" while leaving n=10, so the verdict still graded
              // against 1:10 and could print GREEN beside a message saying the target
              // is a different standard. A verdict that disagrees with its own
              // warning is the one-verdict bug wearing a fourth hat.
              n = 14; out.effective_sub = 'unassisted';
              flags.push({ kind: 'warn', msg: 'Over 190 mm is past step-ramp territory. Recomputed at the main ramp gradient 1:14.' });
            }
          }
        } else if (mode === 'loading' && sc.band) {
          // Size to the green-band ceiling (§ 2b). An operator who knows their
          // machine's spec-plate limit can override it (§ 2b-1, v0.6).
          thetaDeg = sc.band.green;
        } else {
          n = sc.n;
        }

        // LANDINGS (§ 4a). A 1:14 ramp needs a landing every 9 m of RUN, so anything
        // over a ~643 mm rise is not one continuous flight. We sized it as one and
        // printed "Good to go, fine for an NDIS home mod": a standard 900 mm entry
        // needs 12.6 m of run, i.e. two flights and a 1200 mm landing, which is ~15%
        // more run than we quoted and a different product. The fact was already in
        // the content table, but the table isn't what the customer reads: the verdict
        // is. Right information at the right time (§ 2.0a) cuts both ways.
        // Reported as data (out.landings) so the UI states it once, from one source.
        if (mode === 'access' && n === 14) {
          const runAtTarget = rise * n;                 // 1:n means n of run per 1 of rise
          if (runAtTarget > LANDING_MAX_RUN) {
            const flights = Math.ceil(runAtTarget / LANDING_MAX_RUN);
            out.landings = { needed: flights - 1, flights, run_mm: runAtTarget };
            flags.push({ kind: 'warn',
              msg: 'Over 9 m of run, so AS1428.1 wants a landing partway. That’s ' + flights +
                   ' flights with ' + (flights - 1) + ' landing' + (flights > 2 ? 's' : '') +
                   ' between them, not one continuous ramp.' });
          }
        }

        // Required length (§ 1a)
        let required, targetAngle;
        if (mode === 'pet') {
          required = requiredLenAngle(rise, thetaDeg);
          targetAngle = thetaDeg;
          out.target = { theta_deg: thetaDeg, angle_deg: thetaDeg };
        } else if (mode === 'loading' && sc.band) {
          const mc = Number(inp.machine_max_deg);
          // The amber band is green + this sub-case's own spread. It used to be
          // clamped with Math.min(45, ...), which silently COLLAPSED it as mc rose:
          // at mc=45 (a legal entry, the input's own max) amber === green, so the
          // verdict jumped green -> red with no middle. Clamping a BAND EDGE to the
          // same ceiling as the band START is what destroys the band. The input is
          // already bounded at 45; the amber edge is allowed past it, because it's a
          // boundary, not a claim that anything climbs 52°.
          const band = (mc > 0 && mc <= 45)
            ? { green: mc, amber: mc + (sc.band.amber - sc.band.green), custom: true }
            : sc.band;
          required = requiredLenAngle(rise, band.green);
          targetAngle = band.green;
          out.target = { band, angle_deg: band.green };
        } else {
          required = requiredLenRatio(rise, n);
          targetAngle = targetAngleFromN(n);
          out.target = { n, angle_deg: targetAngle };
        }
        out.required_length_mm = required;
        out.diagram = {
          rise_mm: rise,
          run_mm: Math.sqrt(required * required - rise * rise),
          length_mm: required, angle_deg: targetAngle,
        };

        // Quote fork (§ 5c): forklift/yard/container/dock always; plant when ≥ 3 t
        if (mode === 'loading') {
          const heavy = (Number(inp.load_kg) >= QUOTE_CAPACITY_KG) || (Number(inp.capacity_kg) >= QUOTE_CAPACITY_KG);
          if (sc.quote || (sub === 'plant' && heavy)) out.quote_fork = true;
        }

        // Candidate ramp verdict (§ 4)
        const L = Number(inp.length_mm);
        if (L > 0) {
          if (L <= rise) {
            flags.push({ kind: 'error', msg: 'Impossible triangle, a ramp can’t be shorter than the height it climbs.' });
            return out;
          }
          const a = achieved(rise, L);
          out.achieved = a;
          out.diagram = { rise_mm: rise, run_mm: a.run_mm, length_mm: L, angle_deg: a.angle_deg };

          if (mode === 'access') {
            // § 4a: BINARY. Steeper than the target fails. There is no pass band.
            //
            // v0.1-v0.13 spent AS1428.1's "3% tolerance" as a compliance margin and
            // passed down to 1:13.58 as amber, badged "AS1428.1 1:14", captioned
            // "a whisker over the Australian standard, fine for most homes".
            // The clause says the opposite. Cl. 10.3(b): "The gradient of a ramp
            // shall be constant throughout its length with a maximum allowable
            // tolerance of 3% PROVIDED NO SECTION OF THE RAMP IS STEEPER THAN 1 IN
            // 14." It governs CONSISTENCY along the length, and explicitly forbids
            // exceeding 1:14. It is a buildability allowance for the builder pouring
            // the slab, not slack for the gradient we tell a customer to buy.
            // We were citing the clause that forbids it as our authority for doing
            // it, and telling someone whose ramp fails inspection that it's fine.
            // The whole product exists to hand Australians the right number.
            //
            // EPSILON, not tolerance: at exactly required_length, floats land at
            // 13.999999999999996 and graded our OWN answer amber. This closes that
            // and nothing else (see § 9 round-trip case).
            const EPS = 1e-9;
            if (a.ratio_n >= n - EPS) out.verdict = { level: 'green', standard: 'AS1428.1 1:' + n };
            else {
              // Red, but say HOW red: "just misses" is a different conversation from
              // "nowhere near", and both are honest. The number they need is the
              // actionable part, so required_length rides along (out.required_length_mm).
              out.verdict = { level: 'red', standard: 'AS1428.1 1:' + n,
                              near_miss: a.ratio_n >= n * (1 - TOLERANCE) };
              if (sub === 'unassisted') { out.block_cart = true; out.quote_fork = true; }
            }
          } else if (mode === 'threshold') {
            out.verdict = { level: a.ratio_n >= n ? 'green' : 'red', standard: 'AS1428.1 1:' + n };
          } else if (mode === 'pet') {
            // § 4c comfort bands: green ≤ θ−3°, amber (θ−3°, θ], red > θ
            const t = thetaDeg;
            out.verdict = {
              level: a.angle_deg <= t - 3 ? 'green' : (a.angle_deg <= t ? 'amber' : 'red'),
              standard: '≤ ' + t + '° comfort',
            };
          } else { // loading, § 4b v0.3: angle band (or rated gradient) AND load rating
            const load = Number(inp.load_kg), cap = Number(inp.capacity_kg);
            let capacity_ok = null, capacity_margin_low = false;
            if (load > 0 && cap > 0) {
              capacity_ok = cap >= load;
              capacity_margin_low = capacity_ok && cap < load * CAPACITY_MARGIN;
            }
            // Every loading sub-case is a band now (§ 2b v0.6). Verdict from the angle.
            const g = out.target.band.green, am = out.target.band.amber;
            const level0 = a.angle_deg <= g ? 'green' : (a.angle_deg <= am ? 'amber' : 'red');
            const slope_ok = a.angle_deg <= g;
            const standard = 'typical ≤ ' + g + '°';
            let level = level0;
            if (capacity_ok === false) level = 'red';   // under-rated always fails
            out.capacity = { load_kg: load > 0 ? load : null, capacity_kg: cap > 0 ? cap : null,
                             capacity_ok, capacity_margin_low, slope_ok };
            out.verdict = { level, standard };
            if (capacity_ok === null) out.verdict.capacity_pending = true;
            if (capacity_margin_low) {
              flags.push({ kind: 'warn', msg: 'Capacity margin under 20%, consider the next rating up.' });
            }
          }
        } else if (mode === 'loading') {
          // No candidate: capacity check still surfaces as "pending" when load given
          const load = Number(inp.load_kg), cap = Number(inp.capacity_kg);
          if (load > 0 && cap > 0) {
            out.capacity = { load_kg: load, capacity_kg: cap, capacity_ok: cap >= load,
                             capacity_margin_low: cap >= load && cap < load * CAPACITY_MARGIN, slope_ok: null };
            if (out.capacity.capacity_margin_low) {
              flags.push({ kind: 'warn', msg: 'Capacity margin under 20%, consider the next rating up.' });
            }
          }
        }

        return out;
      }

      // The note on the ladder's "← YOURS" row (rules.md § 9, the v0.11 trust bug).
      //
      // The row used to borrow the NEAREST rung's wording by angle. At rise 610 /
      // ramp 7350 (1:12, 4.76°) the nearest wheelchair rung is 1:14 (4.09°), so the
      // row inherited "meets AS1428.1, the Australian standard" and printed it beside
      // a headline that correctly said "Too steep". A compliance claim the ramp does
      // not have, sitting next to the verdict that says it doesn't. Same failure mode
      // fmtRatio's floor exists to prevent: never print a compliant-looking label
      // beside a fail verdict.
      //
      // So: when there IS a target, the row says what OUR verdict says, using the
      // exact string the headline uses. It can't disagree with the headline because
      // it IS the headline's word. Borrowing a neighbour's note is only safe in plain
      // mode, where there is no target and the notes are descriptions ("moderate"),
      // not claims.
      function youNote(ratioLabel, verdictText, nearNote, hasTarget) {
        const tail = hasTarget ? verdictText : nearNote;
        return ratioLabel + (tail ? ' · ' + tail : '');
      }

      // — UNITS (rules.md § top, "one unit rule", v0.8) —
      // Every field is mm and says so. We honour a unit the customer STATES
      // ("1.8m", "180cm") and otherwise read mm, whatever the magnitude.
      // Never infer a unit from size: '60' stays 60, because turning it into 600
      // is the exact silent error RC's brief prices, and it's the same disease as
      // a borrowed verdict — guessing for the customer and being confidently wrong.
      const UNIT_MULT = { mm: 1, cm: 10, m: 1000 };
      function parseMM(raw) {
        if (raw == null) return null;
        const s = String(raw).trim().toLowerCase().replace(/,/g, '').replace(/\s+/g, '');
        if (!s) return null;
        const m = s.match(/^(\d*\.?\d+)(mm|cm|m)?$/);   // mm before m, or 'mm' matches as 'm'+junk
        if (!m) return null;
        const n = parseFloat(m[1]);
        if (!isFinite(n)) return null;
        return n * UNIT_MULT[m[2] || 'mm'];
      }
      // The tell for a wrong-magnitude entry: a unitless decimal. Nobody measures a
      // ramp to a fraction of a millimetre, so '1.8' is almost certainly metres.
      // This drives an ASK (rules.md § top part 5), never a value. Asking != guessing.
      function looksLikeMetres(raw) {
        if (raw == null) return false;
        const s = String(raw).trim().toLowerCase().replace(/,/g, '').replace(/\s+/g, '');
        return /^\d*\.\d+$/.test(s) && parseFloat(s) > 0;
      }

      return { SUBCASES, TOLERANCE, THRESHOLD_MAX_RISE, PET_TALL_RISE,
               requiredLenRatio, requiredLenAngle, targetAngleFromN, achieved, calc, youNote,
               parseMM, looksLikeMetres };
    })();

    // ============================================================
    // SELF-TEST — every rules.md § 9 case, exactly. Runs on every
    // load; prints PASS/FAIL to the console. Add ?selftest=1 to
    // also render the results on the page.
    // ============================================================
    function runRampSelfTest() {
      const r1 = (x) => Math.round(x * 10) / 10;
      const r2 = (x) => Math.round(x * 100) / 100;
      const results = [];
      const T = (name, actual, expected) => {
        const pass = (typeof expected === 'number')
          ? Math.abs(actual - expected) < 1e-9
          : actual === expected;
        results.push({ name, expected, actual, pass });
      };

      // — PLAIN mode (v0.4): rise + length → angle, no target, no verdict —
      let o = RAMP.calc({ mode: 'plain', sub: 'any', rise_mm: 600, length_mm: 2400 });
      T('plain r600 L2400: achieved angle', r2(o.achieved.angle_deg), 14.48);
      T('plain r600 L2400: achieved ratio', r1(o.achieved.ratio_n), 3.9);
      T('plain: no verdict', o.verdict, null);
      T('plain: no required_length', o.required_length_mm, null);
      o = RAMP.calc({ mode: 'plain', sub: 'any', rise_mm: 600 });
      T('plain, no length: asks for one', o.needs_length, true);
      o = RAMP.calc({ mode: 'plain', sub: 'any', rise_mm: 600, length_mm: 400 });
      T('plain, impossible triangle: errors', o.flags.some(f => f.kind === 'error'), true);

      // — Access, unassisted 1:14 (rise 300) —
      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 300 });
      T('access r300: required_length', r1(o.required_length_mm), 4210.7);
      T('access r300: run', r1(o.diagram.run_mm), 4200);
      T('access r300: target angle', r2(o.target.angle_deg), 4.09);

      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 300, length_mm: 4500 });
      T('access r300 L4500: verdict', o.verdict.level, 'green');
      T('access r300 L4500: achieved angle', r2(o.achieved.angle_deg), 3.82);
      T('access r300 L4500: achieved ratio', r1(o.achieved.ratio_n), 15.0);

      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 300, length_mm: 4000 });
      T('access r300 L4000: verdict', o.verdict.level, 'red');
      T('access r300 L4000: achieved angle', r2(o.achieved.angle_deg), 4.3);
      T('access r300 L4000: achieved ratio', r1(o.achieved.ratio_n), 13.3);
      T('access r300 L4000: blocks cart', o.block_cart, true);

      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 300, length_mm: 4100 });
      // Was 'amber' — that expectation encoded the wrong reading of cl. 10.3(b).
      // 1:13.6 is steeper than 1:14, so it fails; near_miss carries the nuance.
      T('access r300 L4100 (1:13.6): FAILS, no pass band', o.verdict.level, 'red');
      T('access r300 L4100: flagged near miss for the copy', o.verdict.near_miss, true);
      T('access r300 L4100: achieved ratio', r1(o.achieved.ratio_n), 13.6);

      // — REGRESSION (v0.11): the ladder's YOURS row must not claim compliance the
      //   ramp doesn't have. Jackie's case: 610 rise up a 7350 ramp is 1:12 / 4.76°,
      //   correctly "Too steep" for a wheelchair, but the row borrowed the nearest
      //   rung (1:14 at 4.09°) and printed "meets AS1428.1" beside the fail. The
      //   poisonous neighbour note is fed in here on purpose: with a target it must
      //   be discarded for our own verdict, never blended in.
      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 610, length_mm: 7350 });
      T('wheelchair r610 L7350 (1:12): verdict', o.verdict.level, 'red');
      T('wheelchair r610 L7350: achieved ratio floors to 1:12', Math.floor(o.achieved.ratio_n), 12);
      const yours = RAMP.youNote('1:12', 'Too steep', 'meets AS1428.1, the Australian standard', true);
      T('wheelchair r610 L7350: YOURS row claims no compliance', yours.indexOf('meets') < 0, true);
      T('wheelchair r610 L7350: YOURS row echoes the headline verdict', yours, '1:12 · Too steep');
      // Plain mode has no target to judge against, so borrowing the nearest rung's
      // plain description ("moderate") is honest and stays.
      T('plain: YOURS row still borrows the nearest description',
        RAMP.youNote('1:12', null, 'moderate', false), '1:12 · moderate');

      // — Threshold 1:8 —
      o = RAMP.calc({ mode: 'threshold', sub: 'doorway', rise_mm: 35 });
      T('threshold r35: required_length', r1(o.required_length_mm), 282.2);
      o = RAMP.calc({ mode: 'threshold', sub: 'doorway', rise_mm: 35, length_mm: 300 });
      T('threshold r35 L300: verdict', o.verdict.level, 'green');
      o = RAMP.calc({ mode: 'threshold', sub: 'doorway', rise_mm: 60 });
      T('threshold r60: warns step-ramp', o.flags.some(f => f.msg.indexOf('step ramp') >= 0), true);
      T('threshold r60: recomputed @1:10', r1(o.required_length_mm), 603.0);

      // — Step ramp 1:10 (Access, assisted/short-rise) —
      o = RAMP.calc({ mode: 'access', sub: 'assisted', rise_mm: 190 });
      T('step r190: required_length', r1(o.required_length_mm), 1909.5);
      T('step r190: target angle', r2(o.target.angle_deg), 5.71);

      // — Loading drive-up band 🟢 ≤22° · 🟠 ≤30° (ute tray 850) —
      o = RAMP.calc({ mode: 'loading', sub: 'driveup', rise_mm: 850 });
      T('driveup r850: required_length (green band)', r1(o.required_length_mm), 2269.0);
      o = RAMP.calc({ mode: 'loading', sub: 'driveup', rise_mm: 850, length_mm: 2300 });
      T('driveup r850 L2300 (Heeve ute ramp): verdict', o.verdict.level, 'green');
      T('driveup r850 L2300: achieved angle', r2(o.achieved.angle_deg), 21.69);
      o = RAMP.calc({ mode: 'loading', sub: 'driveup', rise_mm: 850, length_mm: 2000 });
      T('driveup r850 L2000: verdict', o.verdict.level, 'amber');
      T('driveup r850 L2000: achieved angle', r2(o.achieved.angle_deg), 25.15);
      o = RAMP.calc({ mode: 'loading', sub: 'driveup', rise_mm: 850, length_mm: 1500 });
      T('driveup r850 L1500 (trailer ramp misuse): verdict', o.verdict.level, 'red');
      T('driveup r850 L1500: achieved angle', r2(o.achieved.angle_deg), 34.52);

      // — Loading car band 🟢 ≤10° · 🟠 ≤14° (trailer deck 500) —
      o = RAMP.calc({ mode: 'loading', sub: 'car', rise_mm: 500 });
      T('car r500: required_length (green band)', r1(o.required_length_mm), 2879.4);
      o = RAMP.calc({ mode: 'loading', sub: 'car', rise_mm: 500, length_mm: 2900 });
      T('car r500 L2900: verdict', o.verdict.level, 'green');
      T('car r500 L2900: achieved angle', r2(o.achieved.angle_deg), 9.93);
      o = RAMP.calc({ mode: 'loading', sub: 'car', rise_mm: 500, length_mm: 1800 });
      T('car r500 L1800: verdict (breakover)', o.verdict.level, 'red');

      // — Loading walk-up band 🟢 ≤15° · 🟠 ≤20° (truck deck 1200) —
      o = RAMP.calc({ mode: 'loading', sub: 'walkup', rise_mm: 1200 });
      T('walkup r1200: required_length (green band)', r1(o.required_length_mm), 4636.4);
      o = RAMP.calc({ mode: 'loading', sub: 'walkup', rise_mm: 1200, length_mm: 3500 });
      T('walkup r1200 L3500: verdict (just over band)', o.verdict.level, 'red');

      // — Loading capacity check —
      o = RAMP.calc({ mode: 'loading', sub: 'walkup', rise_mm: 100, length_mm: 1000, load_kg: 180, capacity_kg: 400 });
      T('capacity 400 vs 180: capacity_ok', o.capacity.capacity_ok, true);
      T('capacity 400 vs 180: verdict', o.verdict.level, 'green');
      o = RAMP.calc({ mode: 'loading', sub: 'walkup', rise_mm: 100, length_mm: 1000, load_kg: 180, capacity_kg: 150 });
      T('capacity 150 vs 180: capacity_ok', o.capacity.capacity_ok, false);
      T('capacity 150 vs 180: verdict red though slope fine', o.verdict.level, 'red');

      // — Pet, senior ≤18° —
      o = RAMP.calc({ mode: 'pet', sub: 'senior', rise_mm: 600 });
      T('pet senior r600: required_length', r1(o.required_length_mm), 1941.6);
      o = RAMP.calc({ mode: 'pet', sub: 'senior', rise_mm: 600, length_mm: 2000 });
      T('pet senior r600 L2000: achieved angle', r1(o.achieved.angle_deg), 17.5);
      T('pet senior r600 L2000: within comfort (≤ 18°, not red)', o.verdict.level !== 'red', true);
      o = RAMP.calc({ mode: 'pet', sub: 'senior', rise_mm: 600, length_mm: 1500 });
      T('pet senior r600 L1500: achieved angle', r1(o.achieved.angle_deg), 23.6);
      T('pet senior r600 L1500: verdict', o.verdict.level, 'red');

      // — Pet, young ≤25° —
      o = RAMP.calc({ mode: 'pet', sub: 'young', rise_mm: 600 });
      T('pet young r600: required_length', r1(o.required_length_mm), 1419.7);

      // — Loading PLANT band 🟢 ≤23° · 🟠 ≤30° — RC's published 16–23° (§ 2b-1) —
      o = RAMP.calc({ mode: 'loading', sub: 'plant', rise_mm: 1200 });
      T('plant r1200: required_length (green band)', r1(o.required_length_mm), 3071.2);
      o = RAMP.calc({ mode: 'loading', sub: 'plant', rise_mm: 1200, length_mm: 3500 });
      T('plant r1200 L3500 (Heeve 3.5t): verdict', o.verdict.level, 'green');
      T('plant r1200 L3500: achieved angle', r2(o.achieved.angle_deg), 20.05);
      o = RAMP.calc({ mode: 'loading', sub: 'plant', rise_mm: 1200, length_mm: 2500 });
      T('plant r1200 L2500: verdict', o.verdict.level, 'amber');
      o = RAMP.calc({ mode: 'loading', sub: 'plant', rise_mm: 1200, length_mm: 1800 });
      T('plant r1200 L1800: verdict', o.verdict.level, 'red');

      // — Loading FORKLIFT band 🟢 ≤12° · 🟠 ≤15° — RC's published max 12° (§ 2b-1) —
      o = RAMP.calc({ mode: 'loading', sub: 'forklift', rise_mm: 1300 });
      T('forklift r1300: required_length (green band)', r1(o.required_length_mm), 6252.7);
      T('forklift r1300: quote_fork', o.quote_fork, true);
      o = RAMP.calc({ mode: 'loading', sub: 'forklift', rise_mm: 1300, length_mm: 6300 });
      T('forklift r1300 L6300: verdict', o.verdict.level, 'green');
      T('forklift r1300 L6300: achieved angle', r2(o.achieved.angle_deg), 11.91);
      o = RAMP.calc({ mode: 'loading', sub: 'forklift', rise_mm: 1300, length_mm: 5800 });
      T('forklift r1300 L5800: verdict', o.verdict.level, 'amber');
      T('forklift r1300 L5800: achieved angle', r2(o.achieved.angle_deg), 12.95);
      o = RAMP.calc({ mode: 'loading', sub: 'forklift', rise_mm: 1300, length_mm: 5000 });
      T('forklift r1300 L5000 (15.07°, just over amber): verdict', o.verdict.level, 'red');
      o = RAMP.calc({ mode: 'loading', sub: 'forklift', rise_mm: 1300, length_mm: 4000 });
      T('forklift r1300 L4000: verdict', o.verdict.level, 'red');
      T('forklift w/ candidate: still quote_fork', o.quote_fork, true);

      // — Optional machine-climb override beats the default band (§ 2b-1) —
      o = RAMP.calc({ mode: 'loading', sub: 'plant', rise_mm: 1200, machine_max_deg: 15 });
      T('plant, machine limit 15°: required_length', r1(o.required_length_mm), 4636.4);
      T('plant, machine limit 15°: target', o.target.band.green, 15);

      // — AS1428.1 CORRECTNESS (§ 9, v0.14) — the audit cases. All four of these
      // shipped, and all four told a customer something untrue about the standard
      // the whole product exists to get right.

      // 1. The 3% tolerance is NOT a pass band. Cl. 10.3(b) forbids any section
      //    steeper than 1:14; we were citing it as permission to be steeper.
      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 300, length_mm: 4090 });
      T('1:13.6 is steeper than 1:14, so it FAILS (was amber)', o.verdict.level, 'red');
      T('1:13.6 is flagged a near miss, for the copy only', o.verdict.near_miss, true);
      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 300, length_mm: 3000 });
      T('nowhere near 1:14: red, and NOT a near miss', o.verdict.near_miss, false);

      // 2. Round trip: our own answer must pass our own test. It graded amber,
      //    because floats land at 13.999999999999996 at exactly required_length.
      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 900 });
      const needBack = o.required_length_mm;
      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 900, length_mm: needBack });
      T('our own required_length grades GREEN (float round trip)', o.verdict.level, 'green');

      // 3. Landings. Cl. 10.3(c)(i): a landing every 9 m of run at 1:14. A 900 mm
      //    entry needs 12.6 m of run, so it is two flights, not one continuous ramp.
      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 900 });
      T('900 mm rise: landings reported', !!o.landings, true);
      T('900 mm rise: 2 flights, 1 landing', o.landings && o.landings.flights, 2);
      T('900 mm rise: run over 9 m', o.landings && o.landings.run_mm > 9000, true);
      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 600 });
      T('600 mm rise (8.4 m run): no landing needed', o.landings, undefined);
      // The boundary is 9000/14 = 642.86 mm of rise. Pin BOTH sides of it.
      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 642 });
      T('642 mm rise = 8.99 m run, just under: no landing', o.landings, undefined);
      o = RAMP.calc({ mode: 'access', sub: 'unassisted', rise_mm: 643 });
      T('643 mm rise = 9.00 m run, just over: landing', !!o.landings, true);

      // 4. 'Assisted' 1:10 had no rise guard: a 900 mm entry rendered GREEN badged
      //    AS1428.1 1:10. 1:10 is the STEP-RAMP gradient, capped at a 190 mm rise.
      o = RAMP.calc({ mode: 'access', sub: 'assisted', rise_mm: 900, length_mm: 9045 });
      T('assisted at 900 mm: no longer green', o.verdict.level, 'red');
      T('assisted at 900 mm: recomputed at 1:14', o.target.n, 14);
      T('assisted at 900 mm: warns', o.flags.some(f => f.kind === 'warn'), true);
      o = RAMP.calc({ mode: 'access', sub: 'assisted', rise_mm: 190, length_mm: 1910 });
      T('assisted at 190 mm (the ceiling): still 1:10', o.target.n, 10);
      // Threshold's own >190 path had the same hole: warned "switch to 1:14", kept 1:10.
      o = RAMP.calc({ mode: 'threshold', sub: 'doorway', rise_mm: 300, length_mm: 3020 });
      T('threshold at 300 mm: recomputed at 1:14, not left at 1:10', o.target.n, 14);
      T('threshold at 300 mm: 3020 mm ramp now fails', o.verdict.level, 'red');

      // — ONE VERDICT: the machine-climb override (§ 9, v0.14) —
      // The third instance of the one-verdict bug, and the only one that was unsafe
      // rather than just wrong: the ladder kept its own target table, which had no
      // loading rows, so a 15° machine got a headline of 4.64 m beside a shop button
      // saying 3.07 m. These pin the ENGINE side of the contract the ladder now reads.
      o = RAMP.calc({ mode: 'loading', sub: 'plant', rise_mm: 1200, machine_max_deg: 15 });
      T('plant machine 15°: engine target IS the machine limit', o.target.angle_deg, 15);
      T('plant machine 15°: required_length follows the machine', r1(o.required_length_mm), 4636.4);
      T('plant machine 15°: default 23° length is SHORTER (the bug)',
        RAMP.requiredLenAngle(1200, 23) < o.required_length_mm, true);
      o = RAMP.calc({ mode: 'loading', sub: 'forklift', rise_mm: 1300, machine_max_deg: 8 });
      T('forklift machine 8°: engine target IS the machine limit', o.target.angle_deg, 8);
      T('forklift machine 8°: required_length', r1(o.required_length_mm), 9340.9);
      // The band must not collapse at the input's own maximum (45 is a legal entry).
      o = RAMP.calc({ mode: 'loading', sub: 'plant', rise_mm: 1200, machine_max_deg: 45 });
      T('plant machine 45° (input max): amber band still exists',
        o.target.band.amber > o.target.band.green, true);

      // — UNITS: parseMM never-guess cases (§ 9 "Units", v0.8) —
      // Trust tests, like the one-verdict cases. A stated unit is honoured; a bare
      // number is mm however absurd; magnitude never implies a unit.
      T('parseMM bare 600', RAMP.parseMM('600'), 600);
      T('parseMM bare 18060', RAMP.parseMM('18060'), 18060);
      T('parseMM 1,800 (separator)', RAMP.parseMM('1,800'), 1800);
      T('parseMM 1.8m (stated)', RAMP.parseMM('1.8m'), 1800);
      T('parseMM "1.8 m" (stated, spaced)', RAMP.parseMM('1.8 m'), 1800);
      T('parseMM 180cm (stated)', RAMP.parseMM('180cm'), 1800);
      T('parseMM 1800mm (stated)', RAMP.parseMM('1800mm'), 1800);
      // THE load-bearing case: a bare decimal stays mm. Reading metres off magnitude
      // is the silent 60-vs-600 error RC's brief prices. We ask instead (part 5).
      T('parseMM bare 1.8 stays mm (never guess)', RAMP.parseMM('1.8'), 1.8);
      T('parseMM bare 60 stays 60 (the brief\'s error case)', RAMP.parseMM('60'), 60);
      T('parseMM empty', RAMP.parseMM(''), null);
      T('parseMM junk', RAMP.parseMM('abc'), null);
      T('parseMM unknown unit', RAMP.parseMM('1.8km'), null);
      // looksLikeMetres drives the ASK, never a value
      T('looksLikeMetres 1.8', RAMP.looksLikeMetres('1.8'), true);
      T('looksLikeMetres 0.6', RAMP.looksLikeMetres('0.6'), true);
      T('looksLikeMetres 1800 (no decimal)', RAMP.looksLikeMetres('1800'), false);
      T('looksLikeMetres 1.8m (unit stated, nothing to ask)', RAMP.looksLikeMetres('1.8m'), false);

      const passed = results.filter(x => x.pass).length;
      const lines = results.map(x =>
        (x.pass ? 'PASS' : 'FAIL') + '  ' + x.name +
        (x.pass ? '' : '  (expected ' + x.expected + ', got ' + x.actual + ')'));
      const summary = 'RAMP-CALC SELF-TEST (rules.md § 9): ' + passed + '/' + results.length +
                      (passed === results.length ? ' PASS ✓' : ', FAILURES ABOVE');
      /* eslint-disable no-console */
      if (typeof console !== 'undefined') {
        lines.forEach(l => console.log(l));
        console.log(summary);
      }
      // `failed` + `firstFailure` exist so the error reporter can alert on a broken
      // engine without re-deriving it. Callers must not have to filter results
      // themselves to answer "is the maths wrong right now?".
      const failures = results.filter(x => !x.pass);
      const firstFailure = failures.length
        ? failures[0].name + ' (expected ' + failures[0].expected + ', got ' + failures[0].actual + ')'
        : '';
      return { results, passed, total: results.length, failed: failures.length,
               firstFailure, lines, summary };
    }

    // Node/tooling can require this file's first script block directly.
    if (typeof module !== 'undefined' && module.exports) {
      module.exports = { RAMP, runRampSelfTest };
    }
  


    // ============================================================
    // UI layer — progressive enhancement over the server-rendered
    // default result. All maths lives in RAMP (script block above).
    // ============================================================
    'use strict';


    // CONTEXT (v0.4) — one plain-language row. Optional, never a gate.
    // Each chip maps to an engine mode+sub; the URL can preselect one
    // (?mode=pet) for internal links, but the canonical URL is one page.
    const CTX = [
      { key: 'wheelchair', label: 'Wheelchair',        mode: 'access',    sub: 'unassisted', maxRamp: 4500,
        shop: 'wheelchair-ramps', shopLabel: 'See wheelchair ramps', read: 'wheelchair-ramps', readLabel: 'wheelchair ramps and the standard',
        presets: [{ label: 'One step', rise: 170 }, { label: 'Two steps', rise: 340 }, { label: 'Kerb', rise: 120 }, { label: 'Van floor', rise: 550 }] },
        // 'One step' was 190 while 'Two steps' was 340: the screen disagreeing with
        // itself (190 x 2 = 380). 340 implies 170 a step, so 170 is the value the
        // pair was built around. 190 was also the worst possible default: it is
        // exactly AS1428.1's step-ramp CEILING and exactly our STEP_RAMP_MAX_RISE,
        // so it handed back the most permissive target (1:10) right on the knife
        // edge, and a real 195 mm step is a 1:14 job needing a far longer ramp.
        // A preset should sit in the middle of its case, not on its boundary.
        // STILL TO CONFIRM with Sarah/Tin (§ 7): every preset rise is a house
        // estimate, not an RC-published figure.
      { key: 'dog',        label: 'Dog',               mode: 'pet',       sub: 'senior',     maxRamp: 2200,
        shop: 'dog-ramps', shopLabel: 'See dog ramps', read: 'dog-ramps', readLabel: 'dog ramp angles',
        presets: [{ label: 'Couch', rise: 450 }, { label: 'Bed', rise: 600 }, { label: 'Car boot', rise: 600 }, { label: 'Ute tray', rise: 850 }] },
      { key: 'bike',       label: 'Bike, ATV or mower', mode: 'loading',  sub: 'driveup',    maxRamp: 3500,
        shop: 'motorcycle-ramps', shopLabel: 'See loading ramps', read: 'loading-ramps', readLabel: 'bike, ATV and mower ramps',
        presets: [{ label: 'Ute tray', rise: 850 }, { label: 'Trailer deck', rise: 500 }, { label: 'Truck deck', rise: 1200 }] },
      { key: 'car',        label: 'Car',               mode: 'loading',   sub: 'car',        maxRamp: 4000,
        shop: 'car-truck-loading-ramps', shopLabel: 'See car ramps', read: 'car-ramps', readLabel: 'why car ramps run long',
        presets: [{ label: 'Trailer deck', rise: 500 }, { label: 'Truck deck', rise: 1200 }] },
      { key: 'trolley',    label: 'Trolley or removals', mode: 'loading', sub: 'walkup',     maxRamp: 4500,
        shop: 'removalist-ramps', shopLabel: 'See walk ramps', read: 'loading-ramps', readLabel: 'walk-up ramp angles',
        presets: [{ label: 'Truck deck', rise: 1200 }, { label: 'Trailer deck', rise: 500 }, { label: 'One step', rise: 190 }] },
      { key: 'machinery',  label: 'Plant or machinery', mode: 'loading',  sub: 'plant',      maxRamp: 4000,
        shop: 'construction-machinery', shopLabel: 'See machinery ramps', read: 'plant-ramps', readLabel: 'plant and machinery ramps',
        presets: [{ label: 'Truck deck', rise: 1200 }, { label: 'Trailer deck', rise: 500 }] },
      { key: 'forklift',   label: 'Forklift or container', mode: 'loading', sub: 'forklift', maxRamp: 13000,
        shop: 'forklift-yard-ramps', shopLabel: 'See yard ramps', read: 'plant-ramps', readLabel: 'forklift and container ramps',
        presets: [{ label: 'Container floor', rise: 1300 }, { label: 'Truck deck', rise: 1200 }, { label: 'Loading dock', rise: 1100 }] },
    ];
    const CTX_BY_KEY = {};
    CTX.forEach(c => { CTX_BY_KEY[c.key] = c; });
    // Legacy ?mode= values map onto a chip (internal links from collections)
    const MODE_TO_CTX = { access: 'wheelchair', pet: 'dog', loading: 'bike', threshold: 'wheelchair' };

    // Refinements — asked ONLY on the result, never as a gate. We default to
    // the SAFE answer (strictest) so the question is optional, not required.
    const REFINE = {
      wheelchair: { label: 'Is someone pushing?', opts: [
        { sub: 'unassisted', label: 'No, on their own' },
        { sub: 'assisted',   label: 'Yes, or it’s one small step' } ] },
      dog: { label: 'How’s the dog?', opts: [
        { sub: 'senior', label: 'Older, small or sore' },
        { sub: 'young',  label: 'Young and able' } ] },
    };

    const GENERIC_PRESETS = [
      { label: 'One step', rise: 190 }, { label: 'Car boot', rise: 600 },
      { label: 'Ute tray', rise: 850 }, { label: 'Truck deck', rise: 1200 },
    ];
    const SHOP_BASE = 'https://www.rampchamp.com.au/collections/';
    // RC's real quote page, verified live 16 Jul (200, <h1>Request a Quote</h1>).
    // Was pointed at /pages/contact-us as a placeholder; that's the switchboard, not
    // the quote form, and the dual CTA makes this a permanent path rather than a
    // heavy-gear fallback, so it has to be the right page.
    const QUOTE_URL = 'https://www.rampchamp.com.au/pages/request-a-quote';
    // Every link OFF a result opens in a new tab (Jackie, 16 Jul): "if you go back,
    // we lose the URL... what if they actually want to play around with this again?"
    // The answer they worked for stays put. rel=noopener because target=_blank
    // without it hands the new tab a window.opener handle back to this page.
    const NEW_TAB = ' target="_blank" rel="noopener"';

    // ---- THE LADDER (v3, verdict wiring fixed v6) ----
    // Jackie's Loom: RampAssist shows every gradient at once, colour-coded, with the
    // length each one costs. That beats asking a customer to TYPE a gradient, because
    // a normal person doesn't know one. Rungs are hand-picked per context; the target
    // rung is the one our required_length already uses.
    const LADDERS = {
      plain:      { kind:'ratio', rungs:[20,14,12,10,8,6],
                    notes:{20:'very gentle',14:'gentle',12:'moderate',10:'getting steep',8:'steep',6:'very steep'} },
      wheelchair: { kind:'ratio', rungs:[20,14,10,8,6], target:14,
                    notes:{20:'gentler than you need',14:'meets AS1428.1, the Australian standard',
                           10:'step ramps only, rise up to 190 mm',8:'kerb and threshold ramps, rise up to 190 mm at a kerb, 35 mm at a doorway',
                           6:'too steep for a wheelchair'} },
      dog:        { kind:'deg', rungs:[12,15,18,22,25,30], target:18,
                    notes:{12:'easy, even for a sore old dog',15:'comfortable',18:'the limit for an older dog',
                           22:'young able dogs only',25:'the limit for a young dog',30:'too steep, they will baulk'} },
      bike:       { kind:'deg', rungs:[15,18,22,26,30,35], target:22,
                    notes:{15:'very easy',18:'easy',22:'what these ramps are built to run at',
                           26:'steep but doable with good grip',30:'the practical limit',35:'too steep'} },
      car:        { kind:'deg', rungs:[6,8,10,12,14,18], target:10,
                    notes:{6:'safe for a lowered car',8:'comfortable',10:'fine for most cars',
                           12:'watch the crest',14:'you will likely scrape',18:'it will ground out'} },
      trolley:    { kind:'deg', rungs:[8,12,15,18,22], target:15,
                    notes:{8:'easy push',12:'comfortable',15:'hard work but fine',18:'a real shove',22:'too steep to push'} },
      machinery:  { kind:'deg', rungs:[15,18,23,27,30,35], target:23,
                    notes:{15:'very safe',18:'comfortable',23:'the top of the range Ramp Champ recommends',
                           27:'steeper than we recommend',30:'risky',35:'too steep'} },
      forklift:   { kind:'deg', rungs:[6,8,10,12,15,18], target:12,
                    notes:{6:'ideal for a laden forklift',8:'comfortable',10:'fine',
                           12:'the maximum Ramp Champ recommends',15:'over the limit',18:'the load can slip'} },
    };
    // Sub-case overrides: the target moves with the refinement.
    // EVERY sub-case the engine can land on needs a row here. A missing one silently
    // falls back to L.target and the ladder then marks a different rung to the one
    // the engine sized to, which is the v0.11 trust bug wearing a different hat:
    // `doorway` was missing, so a 35 mm rise (which the engine infers as a threshold
    // ramp at 1:8) got a "Good to go" headline beside a ladder that painted the
    // customer's own 1:8 rung RED and recommended 1:14. Found by sweeping v0.11's fix
    // across every context, not by report.
    // The length we start people at when they give us a height and nothing else
    // (Jackie, 16 Jul): the plain ladder's middle rung, "moderate". Mid-ladder is the
    // honest default precisely BECAUSE we don't know what they're moving yet: it is a
    // place to stand, not a recommendation. The moment they tell us in step 2, the
    // ladder judges it properly. Rounded to the length slider's 10 mm step so the
    // number field and the slider thumb always agree.
    const DEFAULT_LADDER_N = 12;
    const defaultLength = (rise) =>
      Math.round(RAMP.requiredLenRatio(rise, DEFAULT_LADDER_N) / 10) * 10;

    const $ = (id) => document.getElementById(id);
    // ctx = null  → plain angle calculator (the default, no questions asked)
    // lenTouched  → the user has taken the length field off us. Once they have typed
    //               in it, dragged its slider, or deliberately cleared it, we never
    //               write to it again (a cleared field is a request for the ladder,
    //               not an empty state to fill).
    const state = { ctx: null, sub: null, lenTouched: false };

    // — READING A FIELD (rules.md § top, "one unit rule", v0.8) —
    // Every mm field is read through here and nowhere else, so the units law has one
    // implementation, the same way the verdict has one source. parseMM honours a unit
    // the customer states and otherwise reads mm.
    // Returns NaN, not null, when there's no reading: every call site below guards
    // with `> 0` or `|| 0`, and null would coerce to 0 and quietly satisfy a `<= 35`.
    const fieldMM = (id) => { const v = RAMP.parseMM($(id).value); return v == null ? NaN : v; };
    const riseMM = () => fieldMM('rise');
    const lenMM = () => fieldMM('cand-length');

    // Keep the length trailing the height until the user takes it over, so a height
    // always draws a ramp instead of a blank. Only fires from a height change: never
    // on boot (the server-rendered 600 / 2400 worked example is a matched pair and is
    // what Google reads) and never once a context is picked (at that point we know
    // what they're moving, and "moderate" would size a 7 m dog ramp).
    function autofillLength() {
      const rise = riseMM();
      if (state.lenTouched || state.ctx || !(rise > 0)) return;
      $('cand-length').value = defaultLength(rise);
    }

    // — UNITS: the echo + the ask (rules.md § top parts 2 and 5) —
    // Matt, 16 Jul: "18060 is really hard to work out in your head, but below it
    // 1.8 m makes more sense."
    //
    // v0.12 hid the echo below 1000 mm, reasoning that mm is already readable there
    // so an echo would be noise. That was wrong in practice and Matt caught it
    // ("can the how high ALSO have a = 3.49 m section that converts?"): a HEIGHT is
    // almost always under 1000 mm (190 step, 600 boot, 850 ute tray), so the height
    // field, the one he was looking at, was silent nearly always. A rule that fires
    // on one field and not the other is the pattern interrupt, not the cure.
    // So the echo is now ALWAYS on when the value parses. Same shape, every field,
    // every value.
    function renderUnitAids(fieldId, echoId, askId) {
      const raw = $(fieldId).value;
      const mm = RAMP.parseMM(raw);
      const echo = $(echoId);
      echo.textContent = (mm != null && mm > 0) ? '= ' + (mm / 1000).toFixed(2) + ' m' : '';
      echo.classList.toggle('show', !!echo.textContent);

      // The ask. A unitless decimal is the tell for a wrong-magnitude entry: nobody
      // measures a ramp to a fraction of a millimetre. We keep the honest reading
      // and OFFER the fix. Never rewrite their number for them.
      const ask = $(askId);
      if (mm != null && RAMP.looksLikeMetres(raw)) {
        const asMetres = mm * 1000;
        // Plain words. A customer typed 1.8 meaning 1.8 metres; tell them what the
        // box currently says, in a size they can picture, and offer the swap.
        // No similes. "About the width of a pencil" was hardcoded, so it fired for
        // ANY unitless decimal: type 12.5 and the tool insisted 12.5 mm was a pencil.
        // A comparison that's only true for one value is worse than no comparison,
        // and the two numbers side by side already make the point.
        ask.innerHTML = '<span class="warn">Check this</span>' +
          '<span>You’ve typed <b>' + fmtLen(mm) + '</b>. Did you mean <b>' +
          fmtLen(asMetres) + '</b>?</span>' +
          '<button type="button" data-mm="' + asMetres + '">Yes, use ' + fmtLen(asMetres) + '</button>';
        ask.hidden = false;
      } else {
        ask.hidden = true; ask.innerHTML = '';
      }
    }
    function refreshUnitAids() {
      renderUnitAids('rise', 'rise-echo', 'rise-ask');
      renderUnitAids('cand-length', 'len-echo', 'len-ask');
    }

    // — formatting helpers —
    const fmtLen = (mm) => mm >= 1000
      ? (mm / 1000).toFixed(2) + ' m'
      : (Math.round(mm * 10) / 10) + ' mm';
    // Gradient is ALWAYS 1:X with X a whole number (house rule, Matt).
    // FLOOR, never round: 1:13.6 must not display as '1:14' beside a fail verdict.
    const fmtRatio = (n) => '1:' + Math.max(1, Math.floor(n));
    const fmtTargetN = (n) => '1:' + (Number.isInteger(n) ? n : (Math.round(n * 10) / 10));
    const fmtAng = (d) => (Math.round(d * 10) / 10).toFixed(1).replace(/\.0$/, '') + '°';
    const esc = (s) => String(s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' })[c]);

    // — chips —
    function renderCtxChips() {
      $('ctx-chips').innerHTML = CTX.map(c =>
        '<input type="radio" name="ctx" id="ctx-' + c.key + '" value="' + c.key + '"' +
        (state.ctx === c.key ? ' checked' : '') + '>' +
        '<label for="ctx-' + c.key + '">' + esc(c.label) + '</label>').join('');
      document.querySelectorAll('input[name="ctx"]').forEach(el =>
        el.addEventListener('change', e => setCtx(e.target.value)));
      $('clear-ctx').hidden = !state.ctx;
      $('ctx-nudge').hidden = !!state.ctx;
    }

    function renderRefine() {
      const r = state.ctx ? REFINE[state.ctx] : null;
      $('subcase-wrap').hidden = !r;
      if (!r) return;
      $('subcase-label').textContent = r.label;
      const cur = state.sub || CTX_BY_KEY[state.ctx].sub;
      $('subcase-chips').innerHTML = r.opts.map(o =>
        '<input type="radio" name="subcase" id="sub-' + o.sub + '" value="' + o.sub + '"' +
        (o.sub === cur ? ' checked' : '') + '>' +
        '<label for="sub-' + o.sub + '">' + esc(o.label) + '</label>').join('');
      document.querySelectorAll('input[name="subcase"]').forEach(el =>
        el.addEventListener('change', e => { state.sub = e.target.value; calcAndRender(); }));
    }

    function renderPresets() {
      const list = state.ctx ? CTX_BY_KEY[state.ctx].presets : GENERIC_PRESETS;
      $('preset-chips').innerHTML = list.map(p =>
        '<button type="button" class="preset-chip" data-rise="' + p.rise + '">' +
        esc(p.label) + ' <b>' + p.rise + '</b></button>').join('');
      // A preset is a height, so it drags the length along exactly like typing one.
      document.querySelectorAll('.preset-chip').forEach(btn =>
        btn.addEventListener('click', () => {
          $('rise').value = btn.dataset.rise; autofillLength(); calcAndRender();
        }));
    }

    function syncFields() {
      const c = state.ctx ? CTX_BY_KEY[state.ctx] : null;
      const isLoading = !!c && c.mode === 'loading';
      const heavy = isLoading && (c.sub === 'plant' || c.sub === 'forklift');
      $('field-loading-kg').hidden = !isLoading;
      $('field-machine-deg').hidden = !heavy;   // only plant/forklift have a spec-plate climb figure
      if (isLoading) {
        $('load-hint').textContent = heavy
          ? 'machine plus attachments, fluids and you'
          : 'the bike, mower or gear going up';
      }
      // With context, the ramp length becomes optional — we can size it for them.
      // Keep the (mm) through the rewrite: v4's regression was exactly this line
      // dropping it (rules.md § top part 1).
      $('cand-length-label').innerHTML = (c
        ? 'Got a ramp in mind? Its length' : 'How long is the ramp?') +
        ' <span class="unit-tag">(mm)</span> <span class="opt">optional</span>';
      $('cand-length-hint').textContent = c
        ? 'optional. Leave blank and we’ll tell you how long it needs to be'
        : 'the ramp you’ve got or are looking at';
    }

    // — sliders —
    // The useful length range is proportional to the rise (a 35 mm threshold ramp
    // and a 1.3 m container ramp can't share one linear scale), so rescale to the
    // answer: floor at ~56° (steeper is nonsense), ceiling a bit past what they need.
    function lengthRange() {
      const rise = riseMM() || 0;
      if (!(rise > 0)) return { min: 100, max: 5000 };
      let req = null;
      const c = state.ctx ? CTX_BY_KEY[state.ctx] : null;
      if (c) {
        let mode = c.mode, sub = state.sub || c.sub;
        if (mode === 'access' && rise <= 35) { mode = 'threshold'; sub = 'doorway'; }
        const probe = RAMP.calc({ mode, sub, rise_mm: rise, machine_max_deg: parseFloat($('machine-deg').value) || null });
        req = probe.required_length_mm;
      }
      const min = Math.max(10, Math.round(rise * 1.2 / 10) * 10);
      const L = lenMM() || 0;
      // The slider must always reach the longest ramp RC actually sells for this
      // job. req*1.6 alone caps too short at a low rise: plant at a 600 mm rise
      // only NEEDS 1.54 m, but RC's machinery ramps are 3.5 m and 4 m, so the
      // scale stopped at 2.46 m and you couldn't drag to a real product.
      const catMax = c && c.maxRamp ? c.maxRamp : 0;
      let max = Math.round(Math.max(req ? req * 1.6 : rise * 16, catMax) / 10) * 10;
      max = Math.min(Math.max(max, 500), 13000);   // 13 m covers the longest yard ramp
      max = Math.max(max, min + 100, L);           // never strand a typed value off-scale
      return { min, max };
    }

    function syncRanges() {
      const rise = riseMM();
      const rr = $('rise-range');
      const riseMax = rise > 1500 ? Math.ceil(rise / 100) * 100 : 1500;
      rr.max = riseMax;
      $('rise-range-max').textContent = riseMax + ' mm';
      if (rise > 0) rr.value = Math.min(Math.max(rise, rr.min), riseMax);

      const lr = $('len-range');
      const r = lengthRange();
      lr.min = r.min; lr.max = r.max;
      // Same unit as the field directly above: mm. These read metres once, 20 px
      // under a mm field, which is what Matt caught (rules.md § top part 3).
      $('len-range-min').textContent = Math.round(r.min) + ' mm';
      $('len-range-max').textContent = Math.round(r.max) + ' mm';
      const L = lenMM();
      if (L > 0) lr.value = Math.min(Math.max(L, r.min), r.max);
    }

    function setCtx(key) {
      state.ctx = key || null;
      state.sub = null;
      const radio = key ? $('ctx-' + key) : null;
      if (radio) radio.checked = true;
      if (!key) document.querySelectorAll('input[name="ctx"]').forEach(el => { el.checked = false; });
      renderCtxChips(); renderRefine(); renderPresets(); syncFields(); calcAndRender();
    }

    // — SVG diagram (to-scale right triangle; rules.md § 4d) —
    // Labels live OUTSIDE the triangle so nothing collides at flat access
    // angles (a 1:14 ramp is a ~4° sliver).
    function diagramSVG(d) {
      const rise = d.rise_mm, run = d.run_mm, len = d.length_mm, ang = d.angle_deg;
      const boxW = 640, boxH = 240;
      const scale = Math.min(boxW / run, boxH / rise);
      const w = run * scale, h = Math.max(rise * scale, 10);
      const padL = 24, padR = 96, padT = 40, padB = 52;
      const W = w + padL + padR, H = h + padT + padB;
      const x0 = padL, y0 = padT + h;        // toe (bottom-left)
      const x1 = padL + w;                   // bottom-right
      const y2 = padT;                       // top-right
      const visAng = Math.atan2(h, w) * 180 / Math.PI;
      const rad = Math.atan2(h, w);
      const mx = (x0 + x1) / 2, myMid = (y0 + y2) / 2;
      const lx = mx + 12 * Math.sin(rad), ly = myMid - 12 * Math.cos(rad); // ramp label, offset above the hypotenuse
      const arcR = Math.min(64, w * 0.35);
      const ax = x0 + arcR * Math.cos(rad), ay = y0 - arcR * Math.sin(rad);
      const angleLabel = fmtAng(ang) + (d.ratio_label ? ' (' + d.ratio_label + ')' : '');
      return '<svg viewBox="0 0 ' + Math.round(W) + ' ' + Math.round(H) + '" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Side view of the ramp">' +
        '<line class="tri-ground" x1="' + (x0 - 14) + '" y1="' + y0 + '" x2="' + (x1 + 14) + '" y2="' + y0 + '"></line>' +
        '<polygon class="tri-fill" points="' + x0 + ',' + y0 + ' ' + x1 + ',' + y0 + ' ' + x1 + ',' + y2 + '"></polygon>' +
        '<line class="tri-hyp" x1="' + x0 + '" y1="' + y0 + '" x2="' + x1 + '" y2="' + y2 + '"></line>' +
        '<line class="tri-side" x1="' + x1 + '" y1="' + y0 + '" x2="' + x1 + '" y2="' + y2 + '"></line>' +
        (w > 120 ? '<path class="tri-arc" d="M ' + (x0 + arcR).toFixed(1) + ' ' + y0.toFixed(1) +
          ' A ' + arcR.toFixed(1) + ' ' + arcR.toFixed(1) + ' 0 0 0 ' + ax.toFixed(1) + ' ' + ay.toFixed(1) + '"></path>' : '') +
        '<text class="tri-label strong" x="' + lx.toFixed(1) + '" y="' + ly.toFixed(1) + '" text-anchor="middle" transform="rotate(' + (-visAng).toFixed(2) + ' ' + lx.toFixed(1) + ' ' + ly.toFixed(1) + ')">ramp ' + fmtLen(len) + '</text>' +
        '<text class="tri-label angle" x="' + x0 + '" y="' + (y0 + 24) + '">' + angleLabel + '</text>' +
        '<text class="tri-label" x="' + (x0 + w * 0.62).toFixed(1) + '" y="' + (y0 + 24) + '" text-anchor="middle">run ' + fmtLen(run) + '</text>' +
        '<text class="tri-label" x="' + (x1 + 8) + '" y="' + ((y0 + y2) / 2 + 4).toFixed(1) + '">rise ' + Math.round(rise) + ' mm</text>' +
        '</svg>';
    }

    // Build the ladder: every gradient, the length it costs, colour-coded.
    // No candidate needed. This IS the length calculator.
    // `verdictText` is OUR verdict for this ramp, the same string the headline
    // prints. The YOURS row must never invent or borrow its own (see RAMP.youNote).
    // `o` is the ENGINE'S OUTPUT for this exact calculation. The ladder reads its
    // target from it and never derives its own. (rules.md § top, one-verdict rule.)
    //
    // This is the THIRD instance of that rule breaking, and the worst one. The ladder
    // used to look its target up in a hand-maintained LADDER_TARGET table that had
    // rows for `wheelchair` and `dog` and nothing else, so every loading context fell
    // back to the DEFAULT band ceiling and silently ignored `machine_max_deg`. A skid
    // steer with a 15° spec plate up a 1200 mm deck printed a headline saying
    // "you need 4.64 m" beside a shop button saying "look for 3.07 m or longer",
    // and painted the 18° rung green for a machine that tops out at 15°.
    //
    // The first two instances printed a wrong LABEL. This one sold a ramp 1.57 m too
    // SHORT for the machine meant to drive up it. A hand-maintained mirror of the
    // engine's own logic will always drift; the cure is not to add the missing rows,
    // it's to stop having a second table. LADDER_TARGET is deleted.
    function ladderHTML(ctxKey, sub, rise, candLen, verdictText, o) {
      const L = LADDERS[ctxKey || 'plain'];
      if (!L || !(rise > 0)) return '';
      // One source: the engine's target for THIS calc, in this ladder's unit.
      // `n` for ratio ladders, `angle_deg` for degree ladders (which already carries
      // the machine override when there is one). No target = plain mode = no verdict.
      const t = o && o.target;
      const target = !t ? null : (L.kind === 'ratio'
        ? (typeof t.n === 'number' ? t.n : null)
        : (typeof t.angle_deg === 'number' ? t.angle_deg : null));

      const rows = L.rungs.map(r => {
        const len = L.kind === 'ratio' ? RAMP.requiredLenRatio(rise, r) : RAMP.requiredLenAngle(rise, r);
        const deg = L.kind === 'ratio' ? RAMP.targetAngleFromN(r) : r;
        // Colour: at or gentler than the target is green, steeper is red.
        // Without a target (plain) we can't judge, so stay neutral and say so.
        let level = 'neutral';
        if (target !== null) {
          const gentler = L.kind === 'ratio' ? (r >= target) : (r <= target);
          level = gentler ? 'green' : 'red';
        }
        return { r, len, deg, level, rec: false,
                 label: L.kind === 'ratio' ? fmtTargetN(r) : fmtAng(r), note: L.notes[r] || '' };
      });

      // Mark the RECOMMENDED rung.
      //
      // Every DEFAULT target is also a reference rung (wheelchair 14/10/8, dog 18/25,
      // plant 23, forklift 12, bike 22, car 10, trolley 15), so it marks in place and
      // its length already equals the engine's: both sides compute from the same
      // target, so they agree by construction, not by luck.
      //
      // A CUSTOM machine limit (a 15° spec plate) usually falls BETWEEN the rungs.
      // Marking the nearest rung would be a guess, and rules.md says a guessed rung is
      // worse than none. Marking nothing would drop the shop CTA that hangs off it.
      // So the target gets its own row, the same way the customer's ramp does: its
      // label IS the target and its length IS the engine's required_length, so the row
      // can't say 15° beside a length that isn't 15°.
      let recRow = null;
      if (target !== null) {
        recRow = rows.find(x => x.r === target) || null;
        if (!recRow) {
          recRow = {
            r: target,
            len: (o && o.required_length_mm > 0) ? o.required_length_mm
                 : (L.kind === 'ratio' ? RAMP.requiredLenRatio(rise, target)
                                       : RAMP.requiredLenAngle(rise, target)),
            deg: L.kind === 'ratio' ? RAMP.targetAngleFromN(target) : target,
            level: 'green', rec: true, isTarget: true,
            label: L.kind === 'ratio' ? fmtTargetN(target) : fmtAng(target),
            note: 'what your machine is rated to climb',
          };
          rows.push(recRow);
        } else {
          recRow.rec = true;
        }
      }

      // Your ramp, slotted into the ladder where it actually lands.
      //
      // The left column must speak ONE unit the whole way down. It didn't: a
      // wheelchair ladder listed 1:20 / 1:14 / 1:10 and then printed YOUR row as
      // "4°", so the column changed unit exactly once, on the row about you. That's
      // the same defect Matt caught in the standards table ("we start off 1:14 then
      // the rest switch to degrees. No flow"), and it was hiding in the widget.
      // So the row takes the ladder's own unit, and the OTHER unit moves into the
      // note, where it reads as detail rather than a column that changed its mind.
      // (Degree ladders already did this: dog showed "14.5°" + a "1:3 ·" note.)
      let you = null;
      if (candLen > 0 && candLen > rise) {
        const a = RAMP.achieved(rise, candLen);
        const isRatio = L.kind === 'ratio';
        you = { len: candLen, deg: a.angle_deg,
                label: isRatio ? fmtRatio(a.ratio_n) : fmtAng(a.angle_deg),
                ratio: isRatio ? fmtAng(a.angle_deg) : fmtRatio(a.ratio_n) };
      }
      const all = rows.slice();
      if (you) {
        // The row says what the angle MEANS rather than repeating the two numbers
        // already in the columns. WITH a target that meaning is our own verdict, in
        // the headline's own words. WITHOUT one (plain) there is nothing to judge
        // against, so the nearest rung's plain description is the honest read.
        let near = null, best = 1e9;
        rows.forEach(r => { const d = Math.abs(r.deg - you.deg); if (d < best) { best = d; near = r; } });
        all.push({ isYou: true, len: you.len, deg: you.deg, label: you.label,
                   note: RAMP.youNote(you.ratio, verdictText, near && near.note, target !== null) });
      }
      all.sort((x, y) => x.deg - y.deg);   // gentlest at the top, like RampAssist
      const maxLen = Math.max.apply(null, all.map(x => x.len));

      let h = '<div class="ladder"><div class="ladder-head"><span>' +
              (target !== null ? 'Your options' : 'Every gradient, and the ramp it needs') +
              '</span><span>ramp length</span></div>';
      all.forEach(x => {
        const pct = Math.max(4, (x.len / maxLen) * 100);
        const cls = x.isYou ? 'rung you' : ('rung' + (x.rec ? ' rec' : ''));
        const bar = x.isYou ? 'neutral' : x.level;
        h += '<div class="' + cls + '">' +
               '<div class="g">' + x.label + '</div>' +
               '<div><div class="track"><div class="bar ' + bar + '" style="width:' + pct.toFixed(1) + '%"></div></div>' +
                 '<div class="note">' + esc(x.note) + (x.rec ? ' <b>RECOMMENDED</b>' : '') +
                 (x.isYou ? ' <b>← YOURS</b>' : '') + '</div></div>' +
               '<div class="len">' + fmtLen(x.len) + '</div>' +
             '</div>';
      });
      // Jackie's conversion ask: hang the shop link off the recommended rung.
      // recRow is the one resolved above from the ENGINE's target: the CTA length and
      // the headline length are then the same number by construction. Re-finding it
      // here (or re-deriving it) is how the 3.07-vs-4.64 bug happened.
      if (recRow && ctxKey) {
        const c = CTX_BY_KEY[ctxKey];
        // The link goes to the COLLECTION, so the copy must not promise a filtered
        // shelf. "X and longer" read as a filter we don't apply. Matt, 16 Jul: filter
        // URLs died across RC's last theme change, so we only ever link the two URLs
        // Shopify guarantees, /collections/ and /products/. Phase 2 names the 3 ramps
        // that fit (metafields.md) and links products directly; until then we say what
        // to look for.
        // Dual CTA (Matt, 16 Jul): "shop (their result) or request a quote". Not
        // everyone who gets an answer wants to self-serve: heavy gear, odd rises and
        // site jobs need a human, and forcing them through the shelf loses the lead.
        // Both doors, both new tab.
        h += '<div class="ladder-foot">' +
               '<div class="ladder-ctas">' +
                 '<a class="shop" href="' + SHOP_BASE + c.shop + '"' + NEW_TAB + '>' +
                   esc(c.shopLabel) + ', look for ' + fmtLen(recRow.len) + ' or longer →</a>' +
                 '<a class="quote" href="' + QUOTE_URL + '"' + NEW_TAB + '>Ask us for a quote</a>' +
               '</div>' + '</div>';
      } else if (!ctxKey) {
        // Plain mode. Don't state the requirement, offer the upgrade (Jackie, 16 Jul:
        // "tell them that when you pick the type of ramp, we could guide you much
        // better, because that's what's happening here").
        h += '<div class="ladder-foot">Tell us what you’re moving in step 2. '
             + 'Then we’ll tell you which of these lengths is the right one for you.</div>';
      }
      return h + '</div>';
    }

    // — result rendering —
    function calcAndRender() {
      const rise = riseMM();
      const resEl = $('results');
      syncRanges();       // before any early return, so the sliders never desync
      refreshUnitAids();  // same reason: the echo can never disagree with the field

      if (!(rise > 0)) {
        resEl.innerHTML = '<div class="result-hero"><div class="result-kicker">Your ramp angle</div>' +
          '<div class="answer-sub">Pop in a height, or tap one of the common ones, and the angle appears here.</div></div>';
        return;
      }

      const c = state.ctx ? CTX_BY_KEY[state.ctx] : null;
      let mode = c ? c.mode : 'plain';
      let sub  = c ? (state.sub || c.sub) : 'any';
      // Wheelchair + doorway-height rise → it's a threshold ramp. Infer it.
      let inferredThreshold = false;
      if (c && c.mode === 'access' && rise <= 35) { mode = 'threshold'; sub = 'doorway'; inferredThreshold = true; }

      const o = RAMP.calc({
        mode, sub, rise_mm: rise,
        length_mm: lenMM() || null,
        load_kg: parseFloat($('load-kg').value) || null,
        machine_max_deg: parseFloat($('machine-deg').value) || null,
      });

      const flagHtml = o.flags.map(f =>
        '<span class="flag flag-' + (f.kind === 'error' ? 'error' : 'warn') + '">' + esc(f.msg) + '</span>').join('');
      if (o.flags.some(f => f.kind === 'error')) {
        resEl.innerHTML = '<div class="result-hero"><div class="result-kicker">Check that again</div>' +
          '<div style="margin-top:8px;">' + flagHtml + '</div></div>';
        return;
      }

      // ---- PLAIN: no context. Just the angle they came for. ----
      if (!c) {
        if (o.needs_length) {
          // No ramp in mind: show the whole ladder. This is the length calculator,
          // and it needs no gradient knowledge from the user.
          resEl.innerHTML = '<div class="result-hero">' +
            '<div class="result-kicker">How long does your ramp need to be?</div>' +
            '<div class="answer-sub">A <b>' + fmtLen(rise) + '</b> rise. The gentler the ramp, the longer it has to be. ' +
            'Here is every option:</div>' +
            ladderHTML(null, null, rise, null) + '</div>';
          return;
        }
        const a = o.achieved, d = o.diagram;
        resEl.innerHTML = '<div class="result-hero">' +
          '<div class="result-kicker">Your ramp angle</div>' +
          '<div class="answer-big"><span class="num">' + fmtAng(a.angle_deg) + '</span></div>' +
          '<div class="answer-sub">A <b>' + fmtLen(rise) + '</b> rise up a <b>' + fmtLen(d.length_mm) +
            '</b> ramp. That’s a gradient of <b>' + fmtRatio(a.ratio_n) + '</b>.</div>' +
          '<div class="diagram-card">' + diagramSVG(Object.assign({}, d, { ratio_label: fmtRatio(a.ratio_n) })) + '</div>' +
          ladderHTML(null, null, rise, d.length_mm) +
          // The upgrade, sold as help rather than as a missing field (Jackie, 16 Jul).
          '<p class="result-note">Is ' + fmtAng(a.angle_deg) + ' any good? Pick what you’re moving in step 2 ' +
            'and we’ll tell you if this ramp is safe for it, and how long a ramp you actually need.</p>' +
          '</div>';
        return;
      }

      // ---- WITH CONTEXT: the number now means something. ----
      const band = o.target.band || null;
      const isRated = false;   // v0.6: every loading sub-case is a band; no rated-gradient path
      const a = o.achieved;
      const v = o.verdict;
      const level = v ? v.level : null;

      // Headline: the angle if they gave a ramp, else the length they need.
      let html = '<div class="result-hero">';
      html += '<div class="result-kicker">' + esc(c.label) +
              (inferredThreshold ? ' · doorway threshold' : '') + '</div>';

      if (a && v) {
        html += '<div class="answer-big ' + level + '"><span class="num">' + fmtAng(a.angle_deg) + '</span>' +
                '<span class="verdict-word">' + esc(verdictWord(c, level, band, inferredThreshold)) + '</span></div>' +
                '<div class="answer-sub">' + verdictLine(c, o, a, band, isRated, inferredThreshold) + '</div>';
      } else {
        html += '<div class="answer-big"><span class="num">' + fmtLen(o.required_length_mm) + '</span>' +
                '<span class="verdict-word">the ramp you need</span></div>' +
                '<div class="answer-sub">To get a <b>' + fmtLen(rise) + '</b> rise up at ' +
                  targetPhrase(c, o, band, isRated, inferredThreshold) +
                  ', you want a ramp <b>' + fmtLen(o.required_length_mm) + '</b> or longer. Longer is always gentler.</div>';
      }

      if (flagHtml) html += '<div style="margin-top:12px;">' + flagHtml + '</div>';

      // Weight is GUIDANCE, not a check (rules.md § 4b v0.6). We can't check a
      // rating against a ramp they haven't bought, so we tell them what rating to
      // look for. Phase 2 does the real check against the SKU's capacity_kg from
      // the metafield spine.
      const loadKg = parseFloat($('load-kg').value);
      if (loadKg > 0) {
        html += '<div class="check-line"><span class="ok">Look for a ramp rated to at least ' +
          loadKg.toLocaleString('en-AU') + ' kg</span>' +
          ((sub === 'plant' || sub === 'forklift')
            ? ' <span class="pending">(count attachments, fluids and the operator)</span>' : '') +
          '</div>';
      }

      // Diagram
      const d = o.diagram;
      html += '<div class="diagram-card">' +
        diagramSVG(Object.assign({}, d, { ratio_label: a ? fmtRatio(a.ratio_n) : null })) + '</div>';

      // The quiet way out — follow it or don't.
      const need = o.required_length_mm;
      if (o.quote_fork) {
        html += '<div class="shop-line"><a class="q" href="' + QUOTE_URL + '"' + NEW_TAB + '>Get a quote for this one →</a></div>';
      }
      // (the shop link for non-quote paths now hangs off the ladder's recommended rung)

      // The ladder: every option for THIS use, with the right rung marked. The YOURS
      // row gets the headline's own verdict word, so the two can never contradict.
      // `o` is passed so the ladder reads the SAME target the headline just used,
      // including a machine-climb override. It must never look one up itself.
      html += ladderHTML(state.ctx, sub, rise, (lenMM() || null),
                         (a && v) ? verdictWord(c, level, band, inferredThreshold) : null, o);

      // The chip steers the content too: jump to the block that explains this use.
      if (c.read) {
        html += '<div class="shop-line" style="margin-top:8px;"><a href="#' + c.read +
                '" style="font-weight:500;color:var(--mut);">Read more about ' + esc(c.readLabel) + ' ↓</a></div>';
      }

      // "Show me why" — the standards/detail, folded away for the 5% who want it
      html += '<button type="button" class="why-toggle" id="why-toggle">Show me how this is worked out ▾</button>' +
              '<div class="why-body" id="why-body" hidden>' + whyHTML(c, o, a, band, isRated, inferredThreshold) + '</div>';

      html += '</div>';
      resEl.innerHTML = html;

      const wt = $('why-toggle'), wb = $('why-body');
      if (wt) wt.addEventListener('click', () => {
        wb.hidden = !wb.hidden;
        wt.textContent = wb.hidden ? 'Show me how this is worked out ▾' : 'Hide the detail ▴';
      });
    }

    // — copy helpers: plain language first, jargon only inside "show me why" —
    function verdictWord(c, level, band, thr) {
      if (c.mode === 'access') return level === 'green' ? 'Good to go'
        : level === 'amber' ? 'Only just, worth checking' : 'Too steep';
      if (c.mode === 'pet') return level === 'green' ? 'Comfortable for the dog'
        : level === 'amber' ? 'A bit of a climb' : 'Too steep for the dog';
      return level === 'green' ? 'That’ll work'
        : level === 'amber' ? 'Steep, but doable' : 'Too steep';
    }

    function targetPhrase(c, o, band, isRated, thr) {
      if (band && (c.sub === 'plant' || c.sub === 'forklift')) return '<b>' + band.green + '°</b> or less';
      if (band) return 'a comfortable <b>' + band.green + '°</b> or less';
      if (c.mode === 'pet') return '<b>' + o.target.theta_deg + '°</b> or less';
      if (isRated) return 'the ramp’s rated <b>' + fmtTargetN(o.target.n) + '</b>';
      return 'the Australian standard <b>' + fmtTargetN(o.target.n) + '</b>';
    }

    function verdictLine(c, o, a, band, isRated, thr) {
      const need = o.required_length_mm;
      const lvl = o.verdict.level;
      const longer = ' A <b>' + fmtLen(need) + '</b> ramp brings it into the sweet spot.';
      if (c.mode === 'access') {
        // The landing line rides on EVERY access verdict, pass or fail, because a
        // compliant gradient on a ramp that needs a landing is still not a ramp you
        // can build as drawn. It reads from o.landings: one source, stated once.
        const land = o.landings
          ? ' At ' + fmtLen(o.landings.run_mm) + ' of run this needs ' + o.landings.needed +
            ' landing' + (o.landings.needed > 1 ? 's' : '') + ' partway, so it’s ' +
            o.landings.flights + ' flights, not one long ramp.'
          : '';
        if (lvl === 'green') return 'That meets the Australian standard’s gradient for a wheelchair' +
          (thr ? ' at a doorway' : '') + '.' + land;
        // No amber in access mode: steeper than 1:14 does not meet the standard.
        // "Just misses" and "nowhere near" are both red, but they're different
        // conversations, so the copy differs while the verdict doesn't.
        if (o.verdict.near_miss) return 'That just misses the Australian standard, so it won’t pass as a wheelchair ramp.' + longer + land;
        return 'That’s steeper than the Australian standard allows for a wheelchair.' + longer + land;
      }
      if (c.mode === 'pet') {
        if (lvl === 'green') return 'Easy climb for this dog.';
        if (lvl === 'amber') return 'Right on the limit. Fine now, but a longer ramp is kinder as they age.' + longer;
        return 'Too steep. This is the #1 reason dog ramps get sent back.' + longer;
      }
      if (lvl === 'green') return 'Comfortably in the range these ramps are built for.';
      if (lvl === 'amber') return 'Steeper than ideal, but people do load at this angle.' + longer;
      return 'Steeper than these ramps are built for.' + longer;
    }

    function whyHTML(c, o, a, band, isRated, thr) {
      const bits = [];
      if (c.mode === 'access') {
        bits.push('Australian standard <b>AS 1428.1:2021</b> caps a wheelchair ramp at <b>' +
          fmtTargetN(o.target.n) + '</b>' + (thr ? ' for a doorway threshold' : '') +
          '. That’s ' + o.target.n + ' mm of run for every 1 mm of rise (' + fmtAng(o.target.angle_deg) + ').');
        bits.push('The standard allows a 3% tolerance on keeping the gradient even along the ramp, but no part of it may be steeper than 1:14, so we don’t treat that 3% as room to move.');
        bits.push('A permanent ramp also needs landings every 9 m and handrails 865–1000 mm high. This tool sizes the slope; an OT or building certifier signs off the install.');
      } else if (c.mode === 'pet') {
        bits.push('There’s no standard for dog ramps. We use <b>' + o.target.theta_deg +
          '°</b> as the comfort limit for this dog. Older, smaller and post-op dogs struggle above about 18°; young able dogs handle up to about 25°.');
      } else if (band) {
        bits.push('There’s no legal standard for loading ramps. We use <b>' + band.green +
          '°</b> as the working limit for this job. It’s the angle the ramps built for it actually run at.');
      } else if (isRated) {
        bits.push('Plant, forklift, container and dock ramps have no standard angle. Every machine has its own rated maximum, so we size against the ramp’s rated <b>' +
          fmtTargetN(o.target.n) + '</b>, then quote it properly against your machine.');
      }
      bits.push('The maths: ramp length = rise ÷ sin(angle). Your <b>' + fmtLen(o.rise_mm) +
        '</b> rise' + (a ? ' up a <b>' + fmtLen(o.diagram.length_mm) + '</b> ramp sits at <b>' +
        fmtAng(a.angle_deg) + '</b> (' + fmtRatio(a.ratio_n) + ')' : ' needs <b>' + fmtLen(o.required_length_mm) + '</b>') + '.');
      return bits.map(b => '<p style="margin:0 0 8px;">' + b + '</p>').join('');
    }

    // — wiring — (live: everything recalculates as you type; no Calculate button)
    ['load-kg', 'machine-deg'].forEach(id => $(id).addEventListener('input', calcAndRender));
    // The height drags the length along with it, until the user grabs the length.
    $('rise').addEventListener('input', () => { autofillLength(); calcAndRender(); });
    // Typing in the length field is taking it over. So is emptying it.
    $('cand-length').addEventListener('input', () => { state.lenTouched = true; calcAndRender(); });
    // Sliders drive the number fields (which drive everything else)
    $('rise-range').addEventListener('input', e => {
      $('rise').value = e.target.value; autofillLength(); calcAndRender();
    });
    $('len-range').addEventListener('input', e => {
      state.lenTouched = true; $('cand-length').value = e.target.value; calcAndRender();
    });
    // The unit ask (rules.md § top part 5): the customer taps to accept the
    // correction. We never apply it for them. Delegated because the ask is
    // re-rendered on every keystroke.
    [['rise-ask', 'rise'], ['len-ask', 'cand-length']].forEach(([askId, fieldId]) => {
      $(askId).addEventListener('click', e => {
        const btn = e.target.closest('button[data-mm]');
        if (!btn) return;
        $(fieldId).value = btn.dataset.mm;
        if (fieldId === 'cand-length') state.lenTouched = true;   // they set it, it's theirs now
        else autofillLength();
        calcAndRender();
        $(fieldId).focus();
      });
    });

    // Print / save. The browser's own dialog carries "Save as PDF" on every
    // platform, so one button answers both halves of "print/save this".
    // Null-guarded on purpose. This same script ships as a THEME ASSET where the
    // section renders the tool but NOT the page header, so #print-btn doesn't
    // exist. Unguarded, this threw at load and killed every listener after it:
    // the context chips never rendered and the tool was inert. Anything outside
    // the tool itself is optional by definition.
    const printBtn = $('print-btn');
    if (printBtn) printBtn.addEventListener('click', () => window.print());

    $('clear-ctx').addEventListener('click', () => setCtx(null));
    $('reset').addEventListener('click', () => {
      $('rise').value = ''; $('cand-length').value = ''; $('load-kg').value = ''; $('machine-deg').value = '';
      state.lenTouched = false;   // clean slate: the length is ours to fill again
      calcAndRender(); $('rise').focus();
    });
    // Suggestion modal (house pattern — apps/pallet-calc)
    (() => {
      const modal = $('suggest-modal'), openBtn = $('suggest-btn'), cancelBtn = $('sgCancel');
      // Same reason as #print-btn: the modal lives outside the tool and is not
      // shipped to the theme at all (it POSTs a relative submit.php that 404s
      // there). Bail cleanly rather than take the whole script down with it.
      if (!modal || !openBtn) return;
      const form = $('suggest-form'), submitBtn = $('sgSubmit'), status = $('sgStatus'), msgInput = $('sgMessage');
      const show = () => { modal.classList.add('show'); setTimeout(() => $('sgName').focus(), 50); };
      const hide = () => { modal.classList.remove('show'); status.classList.remove('show', 'error', 'success'); status.textContent = ''; };
      const setStatus = (kind, text) => { status.className = 'modal-status show ' + kind; status.textContent = text; };
      openBtn.addEventListener('click', show);
      cancelBtn.addEventListener('click', hide);
      modal.addEventListener('click', e => { if (e.target === modal) hide(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && modal.classList.contains('show')) hide(); });
      form.addEventListener('submit', async e => {
        e.preventDefault();
        if (msgInput.value.trim().length < 10) return setStatus('error', 'Please describe the suggestion in at least a few words.');
        submitBtn.disabled = true; submitBtn.textContent = 'Sending…'; status.classList.remove('show');
        try {
          const fd = new FormData(form);
          const r = await fetch('submit.php', { method: 'POST', body: fd });
          const j = await r.json().catch(() => ({}));
          if (r.ok && j.ok) {
            setStatus('success', '✓ Thanks, your suggestion is on its way.');
            form.reset();
            form.querySelector('input[name="source"]').value = 'ramp-calculator';
            setTimeout(hide, 2200);
          } else setStatus('error', j.error || 'Could not send right now. Try again in a moment.');
        } catch (err) { setStatus('error', 'Network error. Try again in a moment.'); }
        finally { submitBtn.disabled = false; submitBtn.textContent = 'Send suggestion'; }
      });
    })();


    // — boot —
    const params = new URLSearchParams(location.search);
    // ?mode= / ?for= preselect a chip for INTERNAL links (e.g. the dog collection
    // links here with dog already tapped). Canonical stays one URL — see seo-structure.md.
    const urlFor = (params.get('for') || MODE_TO_CTX[(params.get('mode') || '').toLowerCase()] || '').toLowerCase();
    renderCtxChips(); renderRefine(); renderPresets(); syncFields();
    if (CTX_BY_KEY[urlFor]) setCtx(urlFor);

    // ── ERROR REPORTING ───────────────────────────────────────────────────
    // Matt, 16 Jul: "If the tool has an error how will we know? Email to me and
    // Jackie?" Yes, but NOT through an endpoint of our own.
    //
    // The obvious channel was tyreswing.com.au/tools/submit.php, which already
    // emails. Two things kill it: Matt is pulling every tool off the TS site (Astro
    // rebuild, like matthewrscott.com), and this calculator ships INTO the RC
    // Shopify theme, where a same-origin POST to /tools/ 404s anyway. Any endpoint
    // we own is hostage to a host we're tearing down.
    //
    // So we report through the analytics layer the RC theme already loads on every
    // page. No endpoint to own, no CORS, no host to outlive, and it works whether
    // this ends up on rampchamp.com.au, tyreswing.com.au or a future Astro site.
    // An agent reads these events daily and emails Matt + Jackie (HANDOFF § 7).
    //
    // INERT until it's in the theme (no gtag/dataLayer on the preview). By design:
    // there is nothing to watch until the tool is actually in front of customers.
    const errSeen = {};
    function reportError(kind, detail) {
      try {
        const sig = kind + '|' + String(detail).slice(0, 120);
        if (errSeen[sig]) return;           // once per signature per session
        errSeen[sig] = true;
        const payload = {
          error_kind: kind,
          error_detail: String(detail).slice(0, 300),
          // The inputs are what make it reproducible. Without them an alert is just
          // "something broke somewhere", which nobody can act on.
          error_state: 'rise=' + ($('rise') || {}).value + ' len=' + ($('cand-length') || {}).value +
                       ' ctx=' + (state.ctx || 'plain') + '/' + (state.sub || '-'),
        };
        if (typeof window.gtag === 'function') window.gtag('event', 'ramp_calc_error', payload);
        else if (window.dataLayer && window.dataLayer.push)      // GTM, which is how RC loads GA4
          window.dataLayer.push(Object.assign({ event: 'ramp_calc_error' }, payload));
      } catch (_) { /* an error reporter that throws is worse than none */ }
    }
    window.addEventListener('error', e => reportError('js', (e.message || '') + ' @' + (e.lineno || '?')));
    window.addEventListener('unhandledrejection', e => reportError('promise', (e.reason && e.reason.message) || e.reason));

    // Self-test: always to console; ?selftest=1 also renders on the page
    const st = runRampSelfTest();
    // A failing self-test is the alert that matters most (Matt chose this): it means
    // the MATHS is wrong on a customer's screen. The page looks perfectly fine while
    // confidently handing out ramp lengths that don't hold. Everything else is
    // cosmetic next to that.
    if (st.failed > 0) reportError('selftest', st.summary + ' :: ' + st.firstFailure);
    if (params.get('selftest') === '1') {
      // Guarded for the same reason as #print-btn: absent in the theme build.
      // This is the path you reach for WHEN SOMETHING IS WRONG, so it must not be
      // the thing that breaks.
      const out = $('selftest-out');
      if (out) {
      out.textContent = st.lines.join('\n') + '\n\n' + st.summary;
      out.classList.add('show');
      }
    }

    // Hydrate over the server-rendered default (600 mm / 2.4 m = the same
    // numbers, so the first paint matches exactly).
    if (!CTX_BY_KEY[urlFor]) calcAndRender();
  